import { SectionHeader } from "@admin-ui/components/application/section-headers/section-headers";
import { DetailRow } from "@admin-ui/components/composed/detail-row";
import { LifecycleBadge } from "@admin-ui/components/composed/LifecycleBadge";
import { TableEmptyState } from "@admin-ui/components/composed/table-empty-state";
import { Table, TableCard } from "@admin-ui/components/primitives/application/table/table";
import { Badge } from "@admin-ui/components/primitives/base/badges/badges";
import { accountTypeColors } from "@admin-ui/utils/account-display";
import { formatDate } from "@admin-ui/utils/format-date";
import type { AdminUsersUserAccount, AdminUsersUserShow } from "@travily/types/models";
import { Building01 } from "@untitledui/icons";
import { Link, TableBody } from "react-aria-components";

export interface ShowViewProps {
  user: AdminUsersUserShow;
  accounts: AdminUsersUserAccount[];
  adminAccountPath: (id: number) => string;
}

export function ShowView({ accounts, adminAccountPath }: ShowViewProps) {
  const accountCount = `${accounts.length} ${accounts.length === 1 ? "account" : "accounts"}`;

  return (
    <TableCard.Root>
      <TableCard.Header title="Accounts" badge={accountCount} />

      {accounts.length === 0 ? (
        <TableEmptyState
          icon={Building01}
          title="No accounts"
          description="This user is not a member of any accounts."
        />
      ) : (
        <Table aria-label="Accounts">
          <Table.Header>
            <Table.Head id="name" label="Account" isRowHeader />
            <Table.Head id="handle" label="Handle" />
            <Table.Head id="type" label="Type" />
            <Table.Head id="joined" label="Joined" />
          </Table.Header>
          <TableBody items={accounts}>
            {(account: AdminUsersUserAccount) => (
              <Table.Row key={account.id} id={account.id}>
                <Table.Cell>
                  <Link
                    href={adminAccountPath(account.account_id)}
                    className="text-sm font-medium text-brand-600 hover:text-brand-700"
                  >
                    {account.display_name ?? "-"}
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-tertiary">{account.handle ? `@${account.handle}` : "-"}</span>
                </Table.Cell>
                <Table.Cell>
                  <Badge
                    color={
                      account.profile_type
                        ? (accountTypeColors[account.profile_type as keyof typeof accountTypeColors] ?? "gray")
                        : "gray"
                    }
                    size="sm"
                  >
                    {account.profile_type?.toLowerCase() ?? "unknown"}
                  </Badge>
                </Table.Cell>
                <Table.Cell>{formatDate(account.joined_at)}</Table.Cell>
              </Table.Row>
            )}
          </TableBody>
        </Table>
      )}
    </TableCard.Root>
  );
}

export function DetailsView({ user }: { user: AdminUsersUserShow }) {
  return (
    <div className="flex flex-col gap-8 pt-8">
      <div className="flex flex-col gap-6">
        <SectionHeader.Root>
          <SectionHeader.Group>
            <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
              <SectionHeader.Heading>User</SectionHeader.Heading>
              <SectionHeader.Subheading>Authentication and access.</SectionHeader.Subheading>
            </div>
          </SectionHeader.Group>
        </SectionHeader.Root>

        <div className="flex flex-col gap-5">
          <DetailRow label="Email" value={user.email} />
          <DetailRow
            label="Status"
            value={<LifecycleBadge lifecycleState={user.lifecycle_state} anonymizedAt={user.anonymized_at} />}
          />
          <DetailRow
            label="Role"
            value={
              user.admin ? (
                <Badge color="purple" size="sm">
                  admin
                </Badge>
              ) : (
                <span className="text-sm text-tertiary">-</span>
              )
            }
          />
          <DetailRow label="Created" value={formatDate(user.created_at)} />
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <SectionHeader.Root>
          <SectionHeader.Group>
            <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
              <SectionHeader.Heading>Person</SectionHeader.Heading>
              <SectionHeader.Subheading>Personal identity details.</SectionHeader.Subheading>
            </div>
          </SectionHeader.Group>
        </SectionHeader.Root>

        <div className="flex flex-col gap-5">
          <DetailRow label="Name" value={user.name ?? "-"} />
          <DetailRow label="Date of Birth" value={user.person_date_of_birth ?? "-"} />
        </div>
      </div>
    </div>
  );
}
